const URL_SCHEME_WITH_SLASHES_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
const COMMON_NON_SLASH_SCHEME_PATTERN = /^(mailto|tel|sms|data|blob):/i;
const SHORTCODE_PREFIX_PATTERN = /^\[[^\]]+\]/;

type EnsureUrlSchemeOptions = {
    preserveRelative?: boolean;
    preserveShortcodePrefix?: boolean;
};

export const hasExplicitUrlScheme = (value: string): boolean =>
    URL_SCHEME_WITH_SLASHES_PATTERN.test(value) || COMMON_NON_SLASH_SCHEME_PATTERN.test(value);

const stripLeadingUrlScheme = (value: string): string =>
    value.replace(URL_SCHEME_WITH_SLASHES_PATTERN, '').replace(/^\/\//, '');

export const ensureHttpScheme = (
    rawValue?: string | null,
    options: EnsureUrlSchemeOptions = {}
): string => {
    const value = String(rawValue ?? '').trim();
    if (!value) return '';

    if (options.preserveRelative && (value === 'about:blank' || value.startsWith('/') || value.startsWith('#'))) {
        return value;
    }

    if (options.preserveShortcodePrefix !== false && SHORTCODE_PREFIX_PATTERN.test(value)) {
        return value;
    }

    if (value.startsWith('//')) {
        return `http:${value}`;
    }

    if (hasExplicitUrlScheme(value)) {
        return value;
    }

    return `http://${value}`;
};

export const normalizeStoredUrl = (rawValue?: string | null): string =>
    ensureHttpScheme(rawValue, {preserveRelative: true, preserveShortcodePrefix: true});

export const normalizeResolvedUrl = (rawValue?: string | null): string =>
    ensureHttpScheme(rawValue, {preserveRelative: true, preserveShortcodePrefix: true});

export const getUrlTokenReplacement = (
    rawValue: string | null | undefined,
    sourceText: string,
    tokenOffset: number
): string => {
    const value = String(rawValue ?? '').trim();
    const beforeToken = sourceText.slice(0, tokenOffset);
    if (/(?:[a-z][a-z0-9+.-]*:)?\/\/$/i.test(beforeToken)) {
        return stripLeadingUrlScheme(value);
    }
    // Keep token values literal; URL consumers normalize the full resolved URL.
    return value;
};

export const replaceUrlToken = (
    text: string,
    tokenPattern: RegExp,
    rawValue: string | null | undefined
): string =>
    text.replace(tokenPattern, (...args: unknown[]) => {
        const tokenOffset = args[args.length - 2] as number;
        const sourceText = args[args.length - 1] as string;
        return getUrlTokenReplacement(rawValue, sourceText, tokenOffset);
    });
